// src/components/CheckoutBrick.jsx
import React, { useEffect, useRef, useState } from 'react';

/**
 * CheckoutBrick
 * - Container sempre presente (loading = overlay)
 * - Evita init duplo (React 18 StrictMode) com initializedRef
 * - Limpeza correta (unmount) do Brick
 * - Locale pt-BR para labels
 * - Encerra o loading por: onReady, pós-create, MutationObserver e timeout
 */
const normalizeDocumentNumber = (value) => String(value || '').replace(/\D/g, '');

const inferDocumentType = (documentNumber, fallbackType = 'CPF') => {
  const normalizedNumber = normalizeDocumentNumber(documentNumber);

  if (normalizedNumber.length === 14) return 'CNPJ';
  if (normalizedNumber.length === 11) return 'CPF';

  return String(fallbackType || '').toUpperCase() === 'CNPJ' ? 'CNPJ' : 'CPF';
};

const inferEntityType = (documentType) => (documentType === 'CNPJ' ? 'association' : 'individual');

const PAYMENT_STATUS_DETAIL_MESSAGES = {
  cc_rejected_bad_filled_card_number: 'Número do cartão inválido. Verifique e tente novamente.',
  cc_rejected_bad_filled_date: 'Data de validade inválida. Verifique e tente novamente.',
  cc_rejected_bad_filled_other: 'Dados do cartão inválidos. Verifique e tente novamente.',
  cc_rejected_bad_filled_security_code: 'Código de segurança inválido. Verifique e tente novamente.',
  cc_rejected_blacklist: 'Não foi possível processar o pagamento com este cartão.',
  cc_rejected_call_for_authorize: 'Autorize este pagamento com o banco emissor do cartão.',
  cc_rejected_card_disabled: 'Este cartão está desabilitado. Use outro cartão.',
  cc_rejected_card_error: 'Erro ao processar cartão. Tente novamente em alguns minutos.',
  cc_rejected_duplicated_payment: 'Já existe um pagamento semelhante recente. Aguarde e tente novamente.',
  cc_rejected_high_risk: 'Pagamento recusado por segurança. Tente outro cartão.',
  cc_rejected_insufficient_amount: 'Limite insuficiente no cartão.',
  cc_rejected_invalid_installments: 'Este parcelamento não é permitido para o cartão selecionado.',
  cc_rejected_max_attempts: 'Limite de tentativas atingido. Aguarde e tente novamente.',
  cc_rejected_other_reason: 'Pagamento recusado pelo banco emissor.',
};

const getStatusDetailMessage = (statusDetail) =>
  PAYMENT_STATUS_DETAIL_MESSAGES[statusDetail] ||
  'Pagamento não aprovado. Revise os dados do cartão ou tente outro método.';

const CheckoutBrick = ({
  amount,
  payer,
  planData,
  onSuccess,
  onError,
  onPaymentCreated,
  setIsSubmitting,
}) => {
  const CONTAINER_ID = 'cardPaymentBrick_container';
  const MP_PUBLIC_KEY = 'APP_USR-9159cbdb-5403-4d66-b467-9d947465d71e';

  const mpInstanceRef = useRef(null);
  const brickControllerRef = useRef(null);
  const initializedRef = useRef(false);
  const observerRef = useRef(null);
  const timeoutRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);

  // ------- helpers -------
  const waitForContainer = () =>
    new Promise((resolve) => {
      const tryGet = () => {
        const el = document.getElementById(CONTAINER_ID);
        if (el) return resolve(el);
        requestAnimationFrame(tryGet);
      };
      tryGet();
    });

  const loadMercadoPagoSDK = () =>
    new Promise((resolve, reject) => {
      if (window.MercadoPago) return resolve(window.MercadoPago);
      const script = document.createElement('script');
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.async = true;
      script.onload = () =>
        window.MercadoPago
          ? resolve(window.MercadoPago)
          : reject(new Error('Falha ao carregar MercadoPago SDK'));
      script.onerror = () => reject(new Error('Erro ao carregar script do MercadoPago'));
      document.head.appendChild(script);
    });

  const safeUnmount = async () => {
    try {
      if (brickControllerRef.current?.unmount) {
        await brickControllerRef.current.unmount();
      }
    } catch (e) {
      console.warn('Aviso ao desmontar brick:', e?.message || e);
    } finally {
      brickControllerRef.current = null;
    }
  };

  const stopLoadingSafely = () => {
    if (!isLoading) return;
    setIsLoading(false);
    if (observerRef.current) {
      try { observerRef.current.disconnect(); } catch { }
      observerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // ------- main effect -------
  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        if (initializedRef.current) return;
        initializedRef.current = true;

        setIsLoading(true);

        // 1) Container garantido no DOM
        const container = await waitForContainer();

        // 2) Observa quando o Brick injetar nós no container (fallback)
        observerRef.current = new MutationObserver(() => {
          // quando o Brick injeta iframes/divs, consideramos "pronto"
          if (container && container.childNodes.length > 0) {
            stopLoadingSafely();
          }
        });
        observerRef.current.observe(container, { childList: true, subtree: true });

        // 3) Timeout de segurança (fallback)
        timeoutRef.current = setTimeout(() => {
          stopLoadingSafely();
        }, 4000);

        // 4) SDK
        const MP = await loadMercadoPagoSDK();
        mpInstanceRef.current = new MP(MP_PUBLIC_KEY, { locale: 'pt-BR' });

        // 5) Limpa instância velha e zera container
        await safeUnmount();
        container.innerHTML = '';

        // 6) Cria o Brick
        const bricks = mpInstanceRef.current.bricks();
        brickControllerRef.current = await bricks.create('cardPayment', CONTAINER_ID, {
          initialization: {
            amount: Number(amount) || 0,
            payer: {
              email: payer?.email || '',
              identification: {
                type: inferDocumentType(
                  payer?.identification?.number,
                  payer?.identification?.type
                ),
                number: normalizeDocumentNumber(payer?.identification?.number),
              },
            },
          },
          customization: {
            paymentMethods: {
              creditCard: 'all',
              debitCard: 'all',
              mercadoPago: 'all',
            },
            visual: { style: { theme: 'default' } },
            // labels/erros seguem locale pt-BR
          },
          callbacks: {
            onReady: () => {
              if (!isMounted) return;
              stopLoadingSafely();
            },
            onSubmit: async (cardFormData) => {
              await handleSubmit(cardFormData);
            },
            onError: (error) => {
              console.error('❌ Erro no brick:', error);
              onError?.('Erro no formulário de pagamento: ' + (error?.message || ''));
              // mesmo com erro de UI do brick, removemos loading para permitir interação
              stopLoadingSafely();
            },
          },
        });

        // 7) Mesmo se onReady demorar, ao terminar o create já tiramos o loading
        stopLoadingSafely();
      } catch (err) {
        console.error('❌ Erro ao inicializar Mercado Pago:', err);
        onError?.('Erro ao carregar sistema de pagamento. Tente recarregar a página.');
        stopLoadingSafely();
      }
    };

    init();

    return () => {
      isMounted = false;
      safeUnmount();
      if (observerRef.current) {
        try { observerRef.current.disconnect(); } catch { }
        observerRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, payer?.email, payer?.identification?.number, payer?.identification?.type]);

  // ------- submit -------
  const handleSubmit = async (formData) => {
    try {
      setIsSubmitting?.(true);

      if (!formData?.token) {
        throw new Error('Token do cartão não encontrado. Recarregue e tente novamente.');
      }

      const formPayer = formData?.payer || {};
      const formIdentification = formPayer?.identification || {};
      const rawDocumentNumber = formIdentification.number || payer?.identification?.number || '';
      const normalizedDocumentNumber = normalizeDocumentNumber(rawDocumentNumber);
      const documentType = inferDocumentType(
        normalizedDocumentNumber,
        formIdentification.type || payer?.identification?.type
      );

      const payerData = {
        email: formPayer.email || payer?.email || '',
        first_name: formPayer.first_name || payer?.first_name || '',
        last_name: formPayer.last_name || payer?.last_name || '',
        identification: {
          type: documentType,
          number: normalizedDocumentNumber,
        },
        entity_type: inferEntityType(documentType),
      };

      if (!payerData.identification.number) {
        throw new Error('CPF/CNPJ do pagador é obrigatório para processar o pagamento.');
      }

      if (![11, 14].includes(payerData.identification.number.length)) {
        throw new Error('Documento inválido. Informe CPF (11 dígitos) ou CNPJ (14 dígitos).');
      }

      const paymentData = {
        transaction_amount: Number(amount) || 0,
        token: formData.token,
        payment_method_id: formData.payment_method_id,
        installments: parseInt(formData.installments, 10) || 1,
        issuer_id: formData.issuer_id,
        description: `Assinatura ${planData?.name || ''} - EAD Conect`,
        payer: payerData,
        additional_info: {
          plan_id: planData?.id,
          plan_name: planData?.name,
          plan_type: planData?.planType,
        },
      };

      const response = await fetch(
        'https://us-central1-conecta-a8dac.cloudfunctions.net/processarPagamento',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(paymentData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const causeDescription = errorData?.details?.cause?.[0]?.description;
        const detailMessage =
          causeDescription ||
          errorData?.details?.message ||
          errorData?.details?.error ||
          errorData?.message;
        throw new Error(detailMessage || 'Erro ao processar pagamento');
      }

      const paymentResult = await response.json();

      if (paymentResult.status === 'approved') {
        onSuccess?.(paymentResult);
        onPaymentCreated?.(paymentResult);
      } else if (paymentResult.status === 'pending' || paymentResult.status === 'in_process') {
        onPaymentCreated?.(paymentResult);
      } else if (paymentResult.status === 'rejected') {
        throw new Error(getStatusDetailMessage(paymentResult.status_detail));
      } else {
        const status = paymentResult.status || 'desconhecido';
        const statusDetail = paymentResult.status_detail ? ` (${paymentResult.status_detail})` : '';
        throw new Error(`Pagamento com status ${status}${statusDetail}.`);
      }
    } catch (error) {
      onError?.(error?.message || 'Erro ao processar pagamento. Tente novamente.');
    } finally {
      setIsSubmitting?.(false);
    }
  };

  // ------- render -------
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Container sempre presente */}
      <div
        id={CONTAINER_ID}
        style={{
          minHeight: '450px',
          border: '1px solid #e9ecef',
          borderRadius: '12px',
          padding: '1.5rem',
          background: '#ffffff',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        }}
      />

      {/* Overlay do loading (sem remover o container) */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px',
            zIndex: 2,
          }}
        >
          <div
            style={{
              width: '50px',
              height: '50px',
              border: '5px solid #f3f3f4',
              borderTop: '5px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginRight: '12px',
            }}
          />
          <div>
            <h3 style={{ margin: 0, color: '#495057', fontSize: '1.1rem' }}>
              Carregando Pagamento Seguro
            </h3>
            <p style={{ margin: 0, color: '#6c757d' }}>Conectando ao Mercado Pago…</p>
          </div>
          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg) } 100% { transform: rotate(360deg) } }
          `}</style>
        </div>
      )}

      <div
        style={{
          marginTop: '1.5rem',
          padding: '1rem',
          background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
          borderRadius: '10px',
          fontSize: '0.9rem',
          color: '#2e7d32',
          textAlign: 'center',
          border: '1px solid #c8e6c9',
        }}
      >
        🔒 <strong>Pagamento 100% Seguro</strong>
        <br />
        Seus dados são protegidos pelo Mercado Pago
      </div>
    </div>
  );
};

export default CheckoutBrick;
